import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { SHIFT_RELATED_PAGE_PATHS } from '../../utilities/appRoutes';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';

import { RootState } from '../../state/store';
import { settingsAjax } from '../../ajax/SettingsAjax';
import { setShiftEditability } from '../../state/reducers/shiftReducer';
import { ErrorResponse } from '../../DTOs/Common/ErrorResponse';

const ShiftLockBanner = () => {
  const dispatch = useDispatch();
  const [isUnlocking, setIsUnlocking] = useState(false);
  const { pathname } = useLocation();
  const isShiftPage = SHIFT_RELATED_PAGE_PATHS.has(pathname);

  const { isLocked, canToggleLock, selectedDate, selectedShift } = useSelector((s: RootState) => s.shift);

  if (!isShiftPage || !isLocked) return null;

  const date = DateTime.fromISO(selectedDate);
  const dateLabel = date.toFormat('dd MMM yyyy');
  const shiftLabel = selectedShift === 'DAY' ? 'Day' : 'Night';
  const backendCode = selectedShift === 'DAY' ? 'D' : 'N';

  const handleUnlock = async () => {
    setIsUnlocking(true);
    try {
      await settingsAjax.unlockShift(date, backendCode);
      const res = await settingsAjax.getShiftEditability(date, backendCode);
      if (!ErrorResponse.isErrorResponse(res)) {
        dispatch(setShiftEditability(res));
      }
    } finally {
      setIsUnlocking(false);
    }
  };

  return (
    <div className="fixed top-14 left-0 right-0 z-20 flex items-center justify-center gap-3 px-4 py-2 bg-theme-surface-2 border-b border-theme-border text-theme-text text-sm shadow-sm animate-slide-in-down">
      <FontAwesomeIcon icon={faLock} className="shrink-0 text-theme-warning" />
      <span>
        <strong>{dateLabel} · {shiftLabel} shift</strong> is locked — view only
      </span>
      {canToggleLock && (
        <button
          onClick={handleUnlock}
          disabled={isUnlocking}
          className="ml-1 px-3 py-0.5 rounded text-xs font-semibold cursor-pointer bg-theme-warning hover:bg-theme-warning-hover disabled:opacity-50 text-theme-primary-contrast border border-theme-warning-hover transition"
        >
          <FontAwesomeIcon icon={faLockOpen} className="mr-1.5" />
          {isUnlocking ? 'Unlocking…' : 'Unlock'}
        </button>
      )}
    </div>
  );
};

export default ShiftLockBanner;
